import { Component, DoCheck } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ErrorMessageComponent } from '../../../layout/error-message/error-message.component';
import {
  ControlContainer,
  FormControl,
  FormGroupDirective,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  FormInputSelectComponent,
  SelectOption,
} from '../form-input-select.component';
import { ToSelectOptionAttributePipe } from '../../../../pipes/to-select-option-attribute/to-select-option-attribute.pipe';
import { AppendTestIdPipe } from '../../../../pipes/append-test-id/append-test-id.pipe';
import { CheckboxComponent } from '../checkbox/checkbox.component';

@Component({
  selector: 'aw-multi-select',
  imports: [
    CommonModule,
    ErrorMessageComponent,
    ReactiveFormsModule,
    ToSelectOptionAttributePipe,
    AppendTestIdPipe,
    CheckboxComponent,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  templateUrl: './multi-select.component.html',
})
export class MultiSelectComponent
  extends FormInputSelectComponent
  implements DoCheck
{
  protected readonly optionControls: FormControl<boolean>[] = [];

  private readonly attributePipe: ToSelectOptionAttributePipe =
    new ToSelectOptionAttributePipe();

  constructor(public override ngControl: NgControl) {
    super(ngControl);
  }

  ngDoCheck(): void {
    // Reuse controls by index, just like the option views in the template.
    while (this.optionControls.length < this.options().length) {
      this.optionControls.push(
        new FormControl(false, {
          nonNullable: true,
          validators: () => this.ngControl.control!.errors,
        })
      );
    }
    this.optionControls.length = this.options().length;
    const selectedValues = this.pickAttributeFromChosenOptions();
    const disabled = this.ngControl.control!.disabled;
    const errors = this.ngControl.control!.errors;
    const touched = this.ngControl.control!.touched;

    this.optionControls.forEach((control, index) => {
      const selected = selectedValues.includes(
        this.attributePipe.transform(
          this.options()[index],
          this.valueAttribute()
        )
      );
      // Keep the checkbox controls in sync without emitting boolean form values.
      if (control.value !== selected) {
        control.setValue(selected, { emitEvent: false });
      }
      if (control.disabled !== disabled) {
        if (disabled) {
          control.disable({ emitEvent: false });
        } else {
          control.enable({ emitEvent: false });
        }
      }
      if (control.errors !== errors) {
        control.setErrors(errors, { emitEvent: false });
      }
      if (control.touched !== touched) {
        if (touched) {
          control.markAsTouched({ emitEvent: false });
        } else {
          control.markAsUntouched({ emitEvent: false });
        }
      }
    });
  }

  protected pickAttributeFromChosenOptions(): any[] {
    return (
      this.ngControl.control!.value?.map((value: any) =>
        this.attributePipe.transform(value, this.valueAttribute())
      ) ?? []
    );
  }

  public override selectOption(option: SelectOption): void {
    if (!this.ngControl.control!.disabled) {
      const chosenOption = this.attributePipe.transform(
        option,
        this.valueAttribute()
      );
      const presentOptions = this.pickAttributeFromChosenOptions() ?? [];
      const isValuePresent: boolean = presentOptions.includes(chosenOption);

      const newValue = isValuePresent
        ? presentOptions.filter(value => value !== chosenOption)
        : presentOptions.concat([chosenOption]);

      if (this.ngControl.control) {
        this.ngControl.control!.setValue(newValue);
        this.ngControl.control!.markAsTouched();
      }
    }
  }
}
